<template>
  <main class="ps-page">
    <!-- top bar -->
    <header class="ps-auth-top">
      <div class="ps-auth-top__inner">
        <router-link to="/" class="ps-auth-logo" :aria-label="$t('auth.backHome')">
          <img
            src="/images/pos.png"
            alt="PosSmart"
            class="ps-auth-logo__img"
          />
          <span class="ps-auth-logo__text">PosSmart</span>
        </router-link>
        <router-link
          v-if="showBack"
          :to="backTo"
          class="ps-btn ps-btn--ghost ps-auth-back"
        >
          <v-icon small>fal fa-arrow-left</v-icon>
          {{ backLabel }}
        </router-link>
      </div>
    </header>

    <!-- centered card -->
    <main class="ps-auth-body">
      <div class="ps-auth-card" :class="{ 'ps-auth-card--wide': wide }">
        <div class="ps-auth-card__head">
          <span
            class="ps-auth-card__icon"
            :class="icon ? `ps-auth-card__icon--${iconTone}` : 'ps-auth-card__icon--logo'"
          >
            <v-icon v-if="icon" :size="26">{{ icon }}</v-icon>
            <img
              v-else
              src="/images/pos.png"
              alt="PosSmart"
              class="ps-auth-card__logo"
            />
          </span>
          <h1 class="ps-auth-card__title">{{ title }}</h1>
          <p class="ps-auth-card__sub">{{ subtitle }}</p>
        </div>

        <slot />
      </div>

      <p class="ps-auth-copyright">
        {{ copyright }}<template v-if="version"> · v{{ version }}</template>
      </p>
    </main>
  </main>
</template>

<script>
export default {
  name: "AuthShell",
  props: {
    title: { type: String, required: true },
    subtitle: { type: String, default: "" },
    backTo: { type: String, default: "/" },
    backLabel: { type: String, default: "" },
    showBack: { type: Boolean, default: true },
    wide: { type: Boolean, default: false },
    /** FontAwesome class, e.g. "fal fa-rocket" — shown in the icon badge */
    icon: { type: String, default: "" },
    /** brand | lime | soft */
    iconTone: { type: String, default: "brand" },
  },
  computed: {
    version() {
      return process.env.VUE_APP_VERSION;
    },
    copyright() {
      return this.$t("auth.copyright") || "© 2026 PosSmart";
    },
  },
};
</script>

<style scoped>
.ps-auth-top {
  border-bottom: 1px solid var(--ps-slate-300);
  background: var(--ps-cream);
  flex-shrink: 0;
}

.ps-auth-top__inner {
  max-width: 80rem;
  margin: 0 auto;
  padding: 0 1rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
@media (min-width: 640px) {
  .ps-auth-top__inner {
    padding: 0 1.5rem;
  }
}
@media (min-width: 1024px) {
  .ps-auth-top__inner {
    padding: 0 2rem;
  }
}

.ps-auth-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--ps-ink);
}

.ps-auth-logo__img {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid #141021;
  box-shadow: var(--ps-shadow-hard);
  object-fit: cover;
  display: block;
}

.ps-auth-logo__text {
  font-weight: 900;
  font-size: 20px;
  letter-spacing: -0.02em;
}

.ps-auth-back {
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  border-radius: 0.75rem;
  font-weight: 700;
  text-decoration: none;
}

.ps-auth-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

.ps-auth-card {
  width: 100%;
  max-width: 28rem; /* max-w-md */
  background: #fff;
  border: 1px solid #141021;
  border-radius: 1.5rem;
  box-shadow: var(--ps-shadow-hard);
  padding: 2rem;
}
.ps-auth-card--wide {
  max-width: 32rem; /* max-w-lg */
}
@media (min-width: 640px) {
  .ps-auth-card {
    padding: 2.5rem;
  }
}

.ps-auth-card__head {
  text-align: center;
}

.ps-auth-card__icon {
  width: 56px;
  height: 56px;
  border-radius: 1rem;
  border: 1px solid #141021;
  box-shadow: var(--ps-shadow-hard);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}
.ps-auth-card__icon--brand {
  background: var(--ps-brand);
  color: #fff;
}
.ps-auth-card__icon--lime {
  background: var(--ps-lime);
  color: var(--ps-ink);
}
.ps-auth-card__icon--soft {
  background: var(--ps-brand-soft);
  color: var(--ps-brand);
}
/* Logo badge (no icon): white box so the purple mark stays visible */
.ps-auth-card__icon--logo {
  background: #fff;
}
.ps-auth-card__logo {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.ps-auth-card__title {
  margin: 0;
  font-size: 30px;
  font-weight: 900;
  letter-spacing: -0.02em;
  color: var(--ps-ink);
}

.ps-auth-card__sub {
  margin: 8px 0 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--ps-slate-500);
  line-height: 1.6;
}

.ps-auth-copyright {
  margin-top: 24px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: var(--ps-slate-400);
}
</style>
